import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Quote, Trash2, ImagePlus, Instagram } from "lucide-react";
import { useAdminAuth } from "../hooks/useAdminAuth";
import { AdminNav } from "../components/admin/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "h-12 w-full rounded-md bg-seed-off px-4 text-sm text-seed-deep outline-none transition-shadow placeholder:text-seed-deep/40 focus:shadow-[0_0_0_2px_#00D765]";

export default function AdminSite() {
  const navigate = useNavigate();
  const { user, headers, logout } = useAdminAuth();
  const [testimonials, setTestimonials] = useState(null);
  const [posts, setPosts] = useState(null);
  const [tForm, setTForm] = useState({ name: "", city: "", text: "" });
  const [pForm, setPForm] = useState({ image: "", caption: "", link: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try {
      const [t, p] = await Promise.all([
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/instagram-posts`),
      ]);
      setTestimonials(t.data);
      setPosts(p.data);
    } catch {
      setTestimonials([]);
      setPosts([]);
    }
  }, []);

  useEffect(() => {
    if (user && isAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const addTestimonial = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/testimonials`, tForm, { headers: headers() });
      toast.success("Depoimento publicado no site!");
      setTForm({ name: "", city: "", text: "" });
      await load();
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem("seed_admin_token"); navigate("/admin/login"); return; }
      toast.error("Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  const removeTestimonial = async (id) => {
    if (!window.confirm("Excluir este depoimento?")) return;
    await axios.delete(`${API}/testimonials/${id}`, { headers: headers() });
    await load();
  };

  const uploadPostImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/upload`, fd, { headers: headers() });
      setPForm((f) => ({ ...f, image: r.data.url }));
      toast.success("Imagem enviada!");
    } catch {
      toast.error("Falha no envio da imagem.");
    } finally {
      setUploading(false);
    }
  };

  const addPost = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/instagram-posts`, { ...pForm, caption: pForm.caption || null, link: pForm.link || null }, { headers: headers() });
      toast.success("Post publicado no mural do site!");
      setPForm({ image: "", caption: "", link: "" });
      await load();
    } catch {
      toast.error("Não foi possível publicar.");
    } finally {
      setSaving(false);
    }
  };

  const removePost = async (id) => {
    if (!window.confirm("Excluir este post do mural?")) return;
    await axios.delete(`${API}/instagram-posts/${id}`, { headers: headers() });
    await load();
  };

  return (
    <main className="mx-auto min-h-screen max-w-7xl px-6 pt-20 pb-24 lg:px-10" data-testid="admin-site">
      <AdminNav user={user} onLogout={logout} />
      <div className="mt-8 mb-10">
        <p className="text-sm font-bold uppercase tracking-[0.25em] text-seed-salad">Site</p>
        <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter text-seed-deep" data-testid="site-title">
          Depoimentos e mural
        </h1>
        <p className="mt-2 text-sm text-seed-deep/50">Conteúdo que aparece na página inicial do site.</p>
      </div>

      {!isAdmin ? (
        <p className="rounded-lg bg-white p-10 text-center font-semibold text-seed-deep/60 shadow-sm" data-testid="site-restricted">
          Acesso restrito ao administrador.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <section className="rounded-lg bg-white p-8 shadow-sm" data-testid="testimonials-admin">
            <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-seed-deep">
              <Quote size={18} className="text-seed-salad" /> Depoimentos de clientes
            </h2>
            <form onSubmit={addTestimonial} className="space-y-4" data-testid="testimonial-form">
              <div className="grid grid-cols-2 gap-4">
                <input required placeholder="Nome (ex.: Ana Paula)" value={tForm.name} onChange={(e) => setTForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} data-testid="tm-name" />
                <input placeholder="Cidade (ex.: Uberlândia)" value={tForm.city} onChange={(e) => setTForm((f) => ({ ...f, city: e.target.value }))} className={inputCls} data-testid="tm-city" />
              </div>
              <textarea
                required
                rows={3}
                placeholder="O que o cliente disse sobre a Seed Imóveis..."
                value={tForm.text}
                onChange={(e) => setTForm((f) => ({ ...f, text: e.target.value }))}
                className={`${inputCls} h-auto py-3`}
                data-testid="tm-text"
              />
              <button type="submit" disabled={saving} data-testid="tm-submit" className="rounded-full bg-seed-deep px-8 py-3 font-heading text-sm font-bold text-seed-off transition-transform hover:scale-[1.02] disabled:opacity-60">
                Publicar depoimento
              </button>
            </form>
            <div className="mt-6 space-y-3" data-testid="testimonial-list">
              {(testimonials || []).map((t) => (
                <div key={t.id} className="flex items-start justify-between gap-3 rounded-md bg-seed-off p-4" data-testid={`tm-item-${t.id.slice(0, 8)}`}>
                  <div>
                    <p className="text-sm leading-relaxed text-seed-deep/80">“{t.text}”</p>
                    <p className="mt-2 text-xs font-bold text-seed-deep/60">{t.name}{t.city ? ` · ${t.city}` : ""}</p>
                  </div>
                  <button onClick={() => removeTestimonial(t.id)} data-testid={`tm-delete-${t.id.slice(0, 8)}`} className="shrink-0 text-red-400 hover:text-red-600" aria-label="Excluir depoimento">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
              {(testimonials || []).length === 0 && <p className="text-sm text-seed-deep/40">Nenhum depoimento publicado.</p>}
            </div>
          </section>

          <section className="rounded-lg bg-white p-8 shadow-sm" data-testid="instagram-admin">
            <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-seed-deep">
              <Instagram size={18} className="text-seed-salad" /> Mural do Instagram
            </h2>
            <form onSubmit={addPost} className="space-y-4" data-testid="post-form">
              <label className="flex cursor-pointer items-center justify-center gap-3 rounded-md border border-dashed border-seed-salad/50 bg-seed-off/60 p-5 text-sm font-bold text-seed-deep transition-colors hover:text-seed-salad" data-testid="pf-post-upload">
                <input type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={uploadPostImage} data-testid="post-upload-input" />
                <ImagePlus size={18} className="text-seed-salad" />
                {uploading ? "Enviando..." : pForm.image ? "Imagem pronta — trocar" : "Enviar imagem do post"}
              </label>
              {pForm.image && <img src={pForm.image} alt="Post" className="h-32 w-32 rounded-md object-cover" data-testid="post-preview" />}
              <input placeholder="Legenda (opcional)" value={pForm.caption} onChange={(e) => setPForm((f) => ({ ...f, caption: e.target.value }))} className={inputCls} data-testid="post-caption" />
              <input placeholder="Link do post no Instagram (opcional)" value={pForm.link} onChange={(e) => setPForm((f) => ({ ...f, link: e.target.value }))} className={inputCls} data-testid="post-link" />
              <button type="submit" disabled={saving || !pForm.image} data-testid="post-submit" className="rounded-full bg-seed-deep px-8 py-3 font-heading text-sm font-bold text-seed-off transition-transform hover:scale-[1.02] disabled:opacity-60">
                Publicar no mural
              </button>
            </form>
            <div className="mt-6 grid grid-cols-3 gap-3" data-testid="post-list">
              {(posts || []).map((p) => (
                <div key={p.id} className="group relative" data-testid={`post-item-${p.id.slice(0, 8)}`}>
                  <img src={p.image} alt={p.caption || "Post"} className="aspect-square w-full rounded-md object-cover" />
                  <button onClick={() => removePost(p.id)} data-testid={`post-delete-${p.id.slice(0, 8)}`} className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white opacity-0 shadow transition-opacity group-hover:opacity-100" aria-label="Excluir post">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
            {(posts || []).length === 0 && <p className="mt-4 text-sm text-seed-deep/40">Nenhum post no mural.</p>}
          </section>
        </div>
      )}
    </main>
  );
}
